import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import Apiconnection from '../utils/Apiconnection';
import {
  bindVehicleReadyRequestListener,
  createVehicleReadySocket,
} from '../utils/notificationSocket';

const NotificationContext = createContext(null);
const LIVE_NOTIFICATION_TOAST_ID = 'prime-park-live-notification';

const canUseBrowserNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const showBrowserNotification = (notification) => {
  if (!canUseBrowserNotifications() || !document.hidden) {
    return null;
  }

  if (window.Notification.permission !== 'granted') {
    return null;
  }

  const browserNotification = new window.Notification(
    notification?.notiTitle || 'Prime Park Notification',
    {
      body: notification?.notiBody || 'You have a new notification.',
      icon: '/logo192.png',
      tag: notification?.id ? `prime-park-notification-${notification.id}` : undefined,
    }
  );

  browserNotification.onclick = () => {
    window.focus();
    const basePath = process.env.PUBLIC_URL || '';
    window.location.href = `${window.location.origin}${basePath}/notification-list`;
    browserNotification.close();
  };

  return browserNotification;
};

export const showSingleToastNotification = (notification) =>
  toast.info(notification?.notiTitle || 'You have a new notification.', {
    toastId: LIVE_NOTIFICATION_TOAST_ID,
  });

const normalizeNotification = (payload) => {
  if (!payload) {
    return null;
  }

  if (payload.notification) {
    return payload.notification;
  }

  if (payload.data?.notification) {
    return payload.data.notification;
  }

  if (payload.data?.id || payload.data?.notiTitle) {
    return payload.data;
  }

  if (payload.id || payload.notiTitle) {
    return payload;
  }

  if (payload.bookingTicketNo || payload.readyStatus || payload.bookingRequestId) {
    const bookingId = payload.bookingRequestId || payload.id;
    const readyStatusLabel = payload.readyStatus || 'Updated';
    const ticketNumber = payload.bookingTicketNo ? ` Ticket: ${payload.bookingTicketNo}.` : '';

    return {
      id: bookingId || Date.now(),
      bookingRequestId: bookingId,
      notiTitle: `Vehicle ready request ${readyStatusLabel}`,
      notiBody: `Your vehicle-ready request status is now ${readyStatusLabel}.${ticketNumber}`,
      isRead: false,
      createdAt: payload.updatedAt || payload.createdAt || new Date().toISOString(),
      rawPayload: payload,
    };
  }

  return null;
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchNotifications = useCallback(async (page = 1, limit = 10) => {
    const token = localStorage.getItem('prime_access_token');
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return null;
    }

    setIsLoading(true);

    try {
      Apiconnection.defaults.headers.token = token;

      const response = await Apiconnection.post('notification-list', {
        page,
        limit,
      });

      if (response?.data?.status) {
        const notificationData = response.data.data || {};
        const nextNotifications = notificationData.notifications || [];

        setNotifications(nextNotifications);
        setUnreadCount(notificationData.unreadCount || 0);

        return notificationData;
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }

    return null;
  }, []);

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications(1, 10);
  }, [fetchNotifications]);

  const upsertNotification = useCallback((notification) => {
    if (!notification?.id) {
      return;
    }

    setNotifications((currentNotifications) => {
      const existingIndex = currentNotifications.findIndex((item) => item.id === notification.id);

      if (existingIndex === -1) {
        return [notification, ...currentNotifications].slice(0, 10);
      }

      const nextNotifications = [...currentNotifications];
      nextNotifications[existingIndex] = {
        ...nextNotifications[existingIndex],
        ...notification,
      };

      return nextNotifications;
    });

    if (!notification.isRead) {
      setUnreadCount((currentCount) => currentCount + 1);
    }
  }, []);

  const markNotificationReadLocally = useCallback((notificationId) => {
    let shouldDecrement = false;

    setNotifications((currentNotifications) =>
      currentNotifications.map((item) => {
        if (item.id === notificationId && !item.isRead) {
          shouldDecrement = true;
          return { ...item, isRead: true };
        }

        return item;
      })
    );

    if (shouldDecrement) {
      setUnreadCount((currentCount) => Math.max(currentCount - 1, 0));
    }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    const firstUnreadNotification = notifications.find((item) => !item.isRead);

    if (!firstUnreadNotification) {
      return true;
    }

    try {
      await Apiconnection.post('mark-all-notifications-read', {
        notificationId: firstUnreadNotification.id,
        isRead: true,
      });

      setNotifications((currentNotifications) =>
        currentNotifications.map((item) => ({
          ...item,
          isRead: true,
        }))
      );
      setUnreadCount(0);

      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }, [notifications]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    if (!canUseBrowserNotifications()) {
      return;
    }

    if (window.Notification.permission === 'default') {
      window.Notification.requestPermission().catch((error) => {
        console.error('Notification permission request failed:', error);
      });
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('prime_access_token');
    if (!token) {
      return undefined;
    }

    const socketUrl = process.env.REACT_APP_SOCKET_URL || Apiconnection.defaults.baseURL;

    if (!socketUrl) {
      return undefined;
    }

    const socket = createVehicleReadySocket({
      ioClient: io,
      baseURL: socketUrl,
    });

    const handleSocketNotification = (payload) => {
      const notification = normalizeNotification(payload);

      if (!notification) {
        return;
      }

      upsertNotification(notification);
      showBrowserNotification(notification);
      showSingleToastNotification(notification);
    };

    const unbindVehicleReadyRequest = bindVehicleReadyRequestListener(socket, handleSocketNotification);

    socket.on('connect_error', (error) => {
      console.error('Notification socket connection error:', error);
    });

    return () => {
      unbindVehicleReadyRequest();
      socket.disconnect();
    };
  }, [upsertNotification]);

  const contextValue = useMemo(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      fetchNotifications,
      refreshNotifications,
      markNotificationReadLocally,
      markAllNotificationsRead,
      upsertNotification,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      fetchNotifications,
      refreshNotifications,
      markNotificationReadLocally,
      markAllNotificationsRead,
      upsertNotification,
    ]
  );

  return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider.');
  }

  return context;
}
